/**
 * Contracts for error monitoring.
 *
 * Nothing here imports React or any vendor SDK, so tests satisfy these with
 * plain objects and a sink can be written without touching this file.
 */

/** Structured metadata attached to an event. */
export type MonitoringProperties = Record<string, string | number | boolean>;

/**
 * A normalised, **already-redacted** error, ready for any sink.
 *
 * Normalisation happens once, in `./normalise`, so redaction is applied in one
 * place rather than per sink. A sink receiving one of these can assume the text
 * is safe to transmit.
 *
 * Note this covers only errors *we* observe. A sink whose SDK collects errors
 * on its own — Application Insights does — never sees them through here and
 * must redact on its own path too.
 */
export interface ErrorEvent {
  /** Redacted message. */
  message: string;
  /** Redacted stack, when the runtime gave us one. */
  stack?: string;
  /** Constructor name, e.g. `TypeError`. Redacted. */
  name: string;
  /** Whether this stopped the user, as opposed to a handled failure. */
  fatal: boolean;
  /** Redacted caller-supplied metadata. */
  properties?: MonitoringProperties;
}

/** The signed-in user, as much as a sink is allowed to know. */
export interface MonitoringUser {
  /** Opaque user id. Never an email or a name. */
  id: string;
  properties?: MonitoringProperties;
}

/**
 * A destination for errors.
 *
 * The whole vendor surface. Adding a destination means writing one of these and
 * registering it in `./instance` — nothing else in the module changes, and
 * nothing outside the sink imports its SDK.
 *
 * Every method is optional except `report`, so a sink implements only what it
 * can do. All of them must swallow their own errors: this runs on paths that
 * are already failing.
 */
export interface MonitoringSink {
  /** Identifies the sink in logs and in the dev-mode status line. */
  readonly name: string;
  report(event: ErrorEvent): void;
  setUser?(user: MonitoringUser): void;
  flush?(): void;
  dispose?(): void;
}

/** Which sinks to build. `none` disables monitoring entirely. */
export const SinkName = {
  None: 'none',
  Console: 'console',
} as const;
export type SinkName = (typeof SinkName)[keyof typeof SinkName];

/**
 * The `VITE_*` variables `./env-config` reads.
 *
 * Narrower than Vite's `ImportMetaEnv` so tests can pass bare object literals.
 * Sink-specific variables are read by that sink's own config, not here.
 */
export type MonitoringEnv = Partial<
  Record<'VITE_MONITORING_SINKS' | 'VITE_MONITORING_APP_NAME' | 'VITE_RELEASE', string>
>;

/** Vendor-neutral context every sink may want. */
export interface MonitoringConfig {
  /** Which sinks to build, in order. Empty means disabled. */
  sinks: SinkName[];
  /** Logical app name. Sinks map this onto whatever they call a service. */
  appName: string;
  /** Deployed commit SHA, or `local`. */
  release: string;
}

/** Default `appName`, matching `VITE_UNLEASH_APP_NAME`. */
export const DEFAULT_APP_NAME = 'aecom-ai-frontend';

/** Release reported when none is injected at build time. */
export const DEFAULT_RELEASE = 'local';
